// src/services/devService.js

import api from '../fapis/client';
import endpoints from '../fapis/endpoints';

// ============================================================
// HELPER: Format error message
// ============================================================
const formatErrorMessage = (error) => {
  if (error.message === 'Failed to fetch' || error.code === 'ERR_NETWORK') {
    return 'Unable to connect to the server. Please check your internet connection and try again.';
  }
  if (error.code === 'ECONNABORTED' || error.message?.includes('timeout')) {
    return 'The request timed out. Please try again.';
  }
  if (error.response) {
    const status = error.response.status;
    if (status === 403) {
      return error.response.data?.detail || 'You do not have permission to perform this action.';
    }
    if (status === 401) {
      return 'Your session has expired. Please login again.';
    }
    if (status === 404) {
      return error.response.data?.detail || 'Resource not found.';
    }
    return error.response.data?.detail || error.response.data?.message || error.message;
  }
  return error.message || 'An unexpected error occurred. Please try again.';
};

// ============================================================
// DEV SERVICE
// ============================================================
export const devService = {
  /**
   * Get all roles and their permissions for a specific company
   * @param {number} companyId - Company ID
   * @returns {Promise<{success: boolean, data?: Object, message?: string, status?: number}>}
   */
  // src/services/devService.js

getRolePermissionsByCompany: async (companyId) => {
  if (!companyId) {
    return {
      success: false,
      message: 'Company ID is required',
      status: 400
    };
  }

  try {
    const response = await api.get(endpoints.dev.rolePermissions(companyId));
    return {
      success: true,
      data: response.data || { company_id: companyId, roles: [] },
      status: response.status
    };
  } catch (error) {
    console.error('❌ Get role permissions by company error:', error);
    return {
      success: false,
      message: formatErrorMessage(error),
      status: error.response?.status || error.status || 500,
      data: null
    };
  }
},

  /**
   * Get all features for a specific company
   * @param {number} companyId - Company ID
   * @returns {Promise<{success: boolean, data?: Object, message?: string, status?: number}>}
   */
  getCompanyFeatures: async (companyId) => {
    if (!companyId) {
      return {
        success: false,
        message: 'Company ID is required',
        status: 400
      };
    }

    try {
      const response = await api.get(endpoints.dev.companyFeatures(companyId));
      return {
        success: true,
        data: response.data || { company_id: companyId, features: [] },
        status: response.status
      };
    } catch (error) {
      console.error('❌ Get company features error:', error);
      return {
        success: false,
        message: formatErrorMessage(error),
        status: error.response?.status || error.status || 500,
        data: null
      };
    }
  },

  /**
   * Get all companies
   * @returns {Promise<{success: boolean, data?: Array, message?: string, status?: number}>}
   */
  getCompanies: async () => {
    try {
      const response = await api.get(endpoints.dev.companies);
      return {
        success: true,
        data: response.data?.companies || [],
        status: response.status
      };
    } catch (error) {
      console.error('❌ Get companies error:', error);
      return {
        success: false,
        message: formatErrorMessage(error),
        status: error.response?.status || error.status || 500,
        data: []
      };
    }
  },

  /**
   * Get all permissions grouped by feature (Dev version - no auth)
   * @returns {Promise<{success: boolean, data?: Array, message?: string, status?: number}>}
   */
  getPermissionsGroupedDev: async () => {
    try {
      const response = await api.get(endpoints.dev.permissionsGrouped);
      return {
        success: true,
        data: response.data || [],
        status: response.status
      };
    } catch (error) {
      console.error('❌ Get permissions grouped dev error:', error);
      return {
        success: false,
        message: formatErrorMessage(error),
        status: error.response?.status || error.status || 500,
        data: []
      };
    }
  }
};

export default devService;